import { NgModule, APP_INITIALIZER, ErrorHandler, Injector, inject } from '@angular/core';
import { AppInjectorService } from '@shared/services/app-injector.service';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { provideFirestore } from '@angular/fire/firestore';
import { getDatabase,provideDatabase } from '@angular/fire/database';
import { getFunctions,provideFunctions } from '@angular/fire/functions';
import { getMessaging,provideMessaging } from '@angular/fire/messaging';
import { AppCheck, provideAppCheck } from '@angular/fire/app-check';
import { createKzFirestore } from '@shared/services/backend/adapters/firebase/firestore-init';
import { createKzAppCheck, kzAppCheckPlan } from '@shared/services/backend/adapters/firebase/app-check-init';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { SharedModule } from '@shared/shared.module';

import { DynamicFormModule } from '@koomzo/dynamicforms';

import { IonicStorageModule, Storage } from '@ionic/storage-angular';
//import { AgmCoreModule, GoogleMapsAPIWrapper } from '@agm/core'  

import { AppComponent } from './app.component';
import { AppRoutingModule } from '@app/app-routing.module';
import { AppFallbackRoutingModule } from '@app/app-fallback-routing.module';
import { AppLoadModule } from '@app/app-load/app-load.module';
import { AppPreloadingStrategy } from '@app/app-routing.loader';
import { environment } from '@env/environment';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { RouterState } from '@shared/store/router.state';
import { AppState } from '@shared/store/app.state';
import { CapabilityNgxsState } from '@shared/store/capability.state';
import {
  FirebaseStorageService,
  LanguageService,
  UsernameDbService,
  TenantnameDbService,
  KzThemeService
} from '@shared/services/index';
// kz2 M11: from their own files — the product.modules barrel pulls every module it lists (the whole
// Form Builder among them) into the initial chunk.
import { AuthModule } from '@common/auth/client/auth.module';
import { TagsModule } from '@common/tags/server/tags.module';
import { TenantsModule } from '@common/tenants/server/tenants.module';
import { TranslateService, TranslateModule } from '@ngstack/translate';
import { LoggingServiceModule, LoggingService } from 'ionic-logging-service';
import { CoreModule } from '@core/core.module';
import { AppErrorHandler, RollbarService, rollbarFactory, HttpErrorInterceptor, ChunkReloadService, ShellWarmupService, AppUpdateService, OverlayWarmupService, PdfWarmupService } from '@core/index';
import { ServiceWorkerModule } from '@angular/service-worker';
import { kzStrictCsp } from '@shared/services/platform-capability.service';
import { serviceWorkerEnabled } from '@core/services/service-worker-enabled';
import { eagerProductModules } from '@app/app-eager.modules';
import { BaseApplicationStatisticsDbService } from '@shared/services/base-application-statistics-db.service';
import { ApplicationStatisticsDbService,ProviderAppStatisticsDbService } from '@app/features/core/index';
import { BaseProviderAppStatisticsDbService } from '@app/shared/services/base-provider-app-statistics-db.service';
import { BACKEND_PROVIDER, backendAdapterProviders, backendProviderFromConfig, BackendProvider } from '@shared/services/backend';
import { OFFLINE_MODE, OfflineModeService, offlineProviderResolver } from '@shared/services/backend/offline/flag/offline-mode.service';
import { offlineBindingProviders } from '@shared/services/backend/offline/binding/offline-bindings';
import { MOCK_PERSIST } from '@shared/services/backend/adapters/mock/memory-database';
import { InventoryAppStateModel } from '@app/features/erp-apps/inventory/core/store/inventory.state';
import { provideRetailAttention } from '@app/features/erp-apps/point-of-sale/core/retail/retail-attention';
import { provideRestaurantAttention } from '@app/features/erp-apps/restaurant/core/restaurant-attention';
import { provideSalonAttention } from '@app/features/erp-apps/salon/core/salon-attention';
import { provideGroceryAttention } from '@app/features/erp-apps/grocery/core/grocery-attention';
import { provideHotelAttention } from '@app/features/erp-apps/hotel/core/hotel-attention';
import { provideGymAttention } from '@app/features/erp-apps/gym/core/gym-attention';
import { provideSignageAttention } from '@app/features/erp-apps/signage/core/signage-attention';
import { provideMrpAttention } from '@app/features/erp-apps/mrp/core/mrp-attention';
import { provideInvoicingAttention } from '@app/features/erp-apps/invoicing/core/invoicing-attention';
import { provideInvoicingBillingSteps } from '@app/features/erp-apps/invoicing/core/invoicing-billing.provider';
import { provideInventoryAttention } from '@app/features/erp-apps/inventory/core/inventory-attention';
import { kzMark, kzTimed } from '@shared/util/kz-perf';
import { loadTranslationsInParallel } from '@app/app-load/translations.loader';
import { AppBuildGateService } from '@core/update-gate/app-build-gate.service';
import { DesktopRegistrationService } from '@shared/services/backend/offline/device/desktop-registration.service';
import { KzUpdateRequiredComponent } from '@core/update-gate/kz-update-required/kz-update-required.component';

//business modules

export function configureLogging(loggingService: LoggingService): () => void {
  return () => kzTimed('logging', () => loggingService.configure(environment.logging));
}

// needed to load translation before application starts — every file at once (kz2 M12, R11 D)
export function setupTranslateService(service: TranslateService, http: HttpClient) {
  return () =>
    kzTimed('i18n', () => loadTranslationsInParallel(service, (url) => firstValueFrom(http.get(url))));
}

export function initializeStorage(storage: Storage): () => Promise<Storage> {
  return () => kzTimed('storage', () => storage.create());
}

//https://ourcodeworld.com/articles/read/390/how-to-know-if-your-application-is-running-in-cordova-or-a-web-browser
export function isAppPlatform(): boolean {
  return window.hasOwnProperty('cordova') || window.hasOwnProperty('capacitor');
  // return document.location.protocol !== 'http:' && document.location.protocol !== 'https:';
}

export function resolveBackendProvider(): BackendProvider {
  const runtimeProvider = window && window['__env'] ? window['__env'].backendProvider : null;
  return backendProviderFromConfig(runtimeProvider || environment.backendProvider);
}

/**
 * kzd-ac AC-1: this start's App Check, decided once when the root module is declared (app-check-init.ts): reCAPTCHA
 * Enterprise on the web once the project has a key, Play Integrity on Android once the build has its native Firebase
 * config (AC-2, app-check-native.ts), a token minted for the desktop's registered device key once the env turns
 * `appCheck.desktop` on (AC-3, app-check-desktop.ts; review M1), a developer's debug token on dev builds, nothing
 * otherwise (the mock, iOS until it ships, the desktop while it is off, or no key / no native config yet).
 */
const appCheckPlan = kzAppCheckPlan(environment.appCheck, resolveBackendProvider());

const dynamicFormsProviderConfig = {
  BaseUploadService: FirebaseStorageService,
  BaseTranslateService: LanguageService,
  BaseUsernameExistService: UsernameDbService,
  BaseTenantnameExistService: TenantnameDbService,
  ExternalConfig: {
    googleMapApiKey: environment.googleMapsKey,
    orangeMoneyApiKey:null,
    mtnMomoApiKey:null
  }
};

@NgModule({ declarations: [AppComponent, KzUpdateRequiredComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        IonicModule.forRoot(),
        IonicStorageModule.forRoot(),
        AppRoutingModule,
        // provideFirestore(() => {
        //   const firestore = getFirestore();
        //   connectFirestoreEmulator(firestore, 'localhost', 8080);
        //   enableIndexedDbPersistence(firestore);
        //   return firestore;
        // }),
        // kz2 M1: inventory's slice is a root state so its attention projections reach the shell (D158).
        // kzd-of1 P1: no `new Function` selectors under the desktop app's CSP (no 'unsafe-eval').
        NgxsModule.forRoot([RouterState, AppState, CapabilityNgxsState, InventoryAppStateModel], {
            developmentMode: !environment.production,
            compatibility: { strictContentSecurityPolicy: kzStrictCsp() },
        }),
        // kzd prod SM-3: the logger printed every action and the whole state to the prod console; dev builds keep both.
        NgxsLoggerPluginModule.forRoot({ disabled: environment.production }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        LoggingServiceModule,
        SharedModule.forRoot(),
        CoreModule,
        AuthModule.forRoot(),
        AppLoadModule,
        TranslateModule.forRoot({
            //activeLang: 'fr',
            supportedLangs: ['en', 'fr'],
            // `translationRoot` (assets/i18n) is always read first; listing it here fetched it twice.
            translatePaths: ['assets/app/i18n']
        }),
        DynamicFormModule.forRoot(dynamicFormsProviderConfig),
        TagsModule,
        TenantsModule,
        ...eagerProductModules,
        //AgmCoreModule.forRoot({apiKey: environment.googleMapsKey}),
        // kzd-of1 P0-6 (R6 a): production web builds only — never Android, iOS or the Electron desktop app (P1).
        ServiceWorkerModule.register('/combined-sw.js', { enabled: serviceWorkerEnabled() }),
        // kz2 M12 N-2: the root `**` — LAST, after every module that registers root routes.
        AppFallbackRoutingModule], providers: [
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        // kzd-ac AC-1: App Check before Firestore and the rest — AngularFire builds each of them after it. Only when this
        // start has something to start (`appCheckPlan`); it never throws into start-up (createKzAppCheck). A start that
        // failed answers null: AngularFire then holds an empty instance, and every SDK sends its calls without a token.
        ...(appCheckPlan.start ? [provideAppCheck(() => createKzAppCheck(getApp(), appCheckPlan) as AppCheck)] : []),
        // kzd-of1 P0-1: long polling + the persistent device cache (firestore-init.ts). Built only when something injects
        // Firestore — the Firebase backend; the mock never does.
        provideFirestore(() => createKzFirestore(getApp())),
        provideDatabase(() => getDatabase()),
        provideAuth(() => getAuth()),
        // kz2 M9: no provideStorage — the one Storage user (signage media) imports the SDK on demand.
        provideFunctions(() => getFunctions(undefined, 'europe-west2')),
        provideMessaging(() => getMessaging()),
        // needed to load translation before application starts    
        AppPreloadingStrategy,
        // kz2 M1: inventory's rail badges (low stock, counts, in transit) — the first module wired in.
        ...provideInventoryAttention(),
        ...provideRetailAttention(),
        // kz2 M3: the restaurant's Kitchen and Floor badges — root-light signals its store publishes.
        ...provideRestaurantAttention(),
        ...provideSalonAttention(),
        ...provideGroceryAttention(),
        ...provideHotelAttention(),
        ...provideGymAttention(),
        ...provideSignageAttention(),
        ...provideMrpAttention(),
        // kz2 M6: invoicing's badges (overdue, awaiting approval), and its work in the billing run —
        // the device's run under `mock` only (on Firebase the server runs it); both root-light.
        ...provideInvoicingAttention(),
        ...provideInvoicingBillingSteps(),
        //GoogleMapsAPIWrapper,
        {
            // Apply the persisted theme (data-kz-theme) before the first render.
            provide: APP_INITIALIZER,
            useFactory: (kz: KzThemeService) => () => kzTimed('theme', () => kz.init()),
            deps: [KzThemeService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: setupTranslateService,
            deps: [TranslateService, HttpClient],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeStorage,
            deps: [Storage],
            multi: true
        },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        // { provide: IS_APP_PLATFORM, useFactory: isAppPlatform },
        { provide: 'isApp', useFactory: isAppPlatform },
        {
            deps: [LoggingService],
            multi: true,
            provide: APP_INITIALIZER,
            useFactory: configureLogging
        },
        {
            provide: RollbarService,
            useFactory: rollbarFactory
        },
        {
            provide: ErrorHandler,
            useClass: AppErrorHandler
        },
        {
            // A tab left open across a deploy asks for the old build's chunks: reload once (core/services/chunk-reload.service.ts).
            provide: APP_INITIALIZER,
            useFactory: (chunks: ChunkReloadService) => () => kzTimed('chunk-reload', () => chunks.watch()),
            deps: [ChunkReloadService],
            multi: true
        },
        {
            // kzd-of1 P0-6: on the visit that installs the web service worker, re-ask for the start-up files once it
            // controls the page, so an offline reload after ONE visit boots (core/services/shell-warmup.service.ts).
            provide: APP_INITIALIZER,
            useFactory: (warmup: ShellWarmupService) => () => warmup.start(),
            deps: [ShellWarmupService],
            multi: true
        },
        {
            // kzd-of1 walk F1: load the alert, loading and toast overlays' lazy code while online, so a sign-out's
            // question (and the offline notice) can still be shown offline (core/services/overlay-warmup.service.ts).
            provide: APP_INITIALIZER,
            useFactory: (overlays: OverlayWarmupService) => () => overlays.start(),
            deps: [OverlayWarmupService],
            multi: true
        },
        {
            // FU-of1-58: load pdfMake and its fonts once per build through the web service worker, so a receipt's PDF
            // can be made offline (core/services/pdf-warmup.service.ts).
            provide: APP_INITIALIZER,
            useFactory: (pdf: PdfWarmupService) => () => pdf.start(),
            deps: [PdfWarmupService],
            multi: true
        },
        {
            // kzd-of1 P0-6 (review WI-7 #1): a deploy reaches an open tab — "A new version is ready · Reload", and a
            // check for one when the tab comes back, at most every 30 min (core/services/app-update.service.ts).
            provide: APP_INITIALIZER,
            useFactory: (updates: AppUpdateService) => () => updates.watch(),
            deps: [AppUpdateService],
            multi: true
        },
        {
            // kzd-ac AC-R6: the minimum-version gate — "Update required" over the app when this Android, iOS or desktop
            // build is below `minAppBuild` (core/update-gate). Started, never awaited; the web is never gated.
            provide: APP_INITIALIZER,
            useFactory: (gate: AppBuildGateService) => () => gate.start(),
            deps: [AppBuildGateService],
            multi: true
        },
        {
            // kzd-ac AC-3 (ruling AC-R1 a): the desktop registers itself on an owner's or a manager's signed-in start, so
            // it can be minted App Check tokens; staff see a one-time notice. Desktop + Firebase only, and only once
            // `environment.appCheck.desktop` is on (review M1); never awaited.
            provide: APP_INITIALIZER,
            useFactory: (desktop: DesktopRegistrationService) => () => void desktop.start(),
            deps: [DesktopRegistrationService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        {
            provide: BACKEND_PROVIDER,
            useFactory: resolveBackendProvider
        },
        /* kzd of2 P2-2: the offline flag beside the backend resolver — `offlineProviderResolver()` decides this start's
           offline mode (off | standalone | localNetwork, and drain-only) from the build, the kill switch, the business and
           the device. P2 only decides; P2-10 wires the (lazy) offline adapter from `OFFLINE_MODE`. */
        {
            provide: OFFLINE_MODE,
            useFactory: offlineProviderResolver
        },
        {
            // Started at start-up, never awaited: it decides once the first business signs in.
            provide: APP_INITIALIZER,
            useFactory: (mode: OfflineModeService) => () => void mode,
            deps: [OFFLINE_MODE],
            multi: true
        },
        /* kzd of2 P2-11: the offline runtime's platform store and pull feed — tiny eager bindings whose code loads only when
           a native start resolves offline (P2-R12). */
        ...offlineBindingProviders,
        ...backendAdapterProviders,
        /* The mock app keeps the App Selector, custom roles and the Control Centre across a reload — every collection in
           keep-all mode (`?mockKeepAll=1`, kzd fast lane S2); `?mockReset=1` clears them. */
        {
            provide: MOCK_PERSIST,
            useFactory: () => inject(BACKEND_PROVIDER) === 'mock'
        },
        {
            provide: BaseApplicationStatisticsDbService,
            useClass: ApplicationStatisticsDbService
        },
        {
            provide: BaseProviderAppStatisticsDbService,
            useClass: ProviderAppStatisticsDbService
        },
        //https://www.leonelngande.com/how-to-get-a-reference-to-the-window-object-in-an-angular-8-application/
        { provide: Window, useValue: window },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
  /**
   * BasePage reads its services from AppInjectorService, and main.ts only set it after
   * bootstrapModule() resolved. The first navigation runs DURING bootstrap. While `''` lazy-loaded
   * the introduction module, the first page was built after that promise. Since kz2 F1, `''`
   * redirects to the eager /login page, which is built first. Setting the injector here, when the
   * root module is instantiated, is before any route component exists.
   */
  constructor(injector: Injector) {
    kzMark('app-module');
    AppInjectorService.setInjector(injector);
  }
}
